import {
	type SalesChannelCategoryQopFilterInput,
	useSalesChannelCategoriesQuery,
} from "@/generated/graphql";
import type { PaginationState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

// Server-paged category list, always bound to one category group.
export const useSalesChannelCategoryPagedList = (
	groupID: string | undefined,
) => {
	const [paginationState, setPaginationState] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [filtersState, setFiltersState] = useState<Record<string, unknown>>({});

	const options = useMemo(
		() => ({
			variables: {
				qop: {
					pagination: {
						page: paginationState.pageIndex + 1,
						pageSize: paginationState.pageSize,
						withCount: true,
					},
					sorts: [{ field: "display_order", direction: "asc" }],
					filters: {
						...filtersState,
						salesChannelCategoryGroupID: groupID ?? null,
					} as SalesChannelCategoryQopFilterInput,
				},
			},
			pause: !groupID,
			requestPolicy: "network-only" as const,
			context: { suspense: false },
		}),
		[
			paginationState.pageIndex,
			paginationState.pageSize,
			filtersState,
			groupID,
		],
	);

	const [{ data, error, fetching }, fetch] =
		useSalesChannelCategoriesQuery(options);

	const load = useCallback(
		() => fetch({ requestPolicy: "network-only" }),
		[fetch],
	);

	const setPagination = useCallback(
		(next: PaginationState) => setPaginationState(next),
		[],
	);

	const setFilters = useCallback((next: Record<string, unknown>) => {
		setFiltersState(next);
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, []);

	return {
		items: data?.salesChannelCategories.items ?? [],
		rowCount: data?.salesChannelCategories.pagination.totalRows ?? 0,
		fetching,
		error,
		load,
		paginationState,
		setPagination,
		filtersState,
		setFilters,
	};
};
